import React, { useState, useEffect } from 'react';
import { Input } from 'antd';
import styled from 'styled-components';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { TAB_AUDIENCE } from 'constants/paths';
import ResearchInvertedIcon from 'assets/svgs/ResearchInvertedIcon';
import { maxWidth, maxWidth1300, primaryColor, primaryFont, retina, wLight } from 'constants/utilsStyles';
import CrossIcon from 'assets/svgs/CrossIcon';
import SearchIcon from 'assets/svgs/SearchIcon';
import { getAudienceSearchedListsAction, resetAudienceSearchedList } from '../../Audience/Actions/listsActions';
import { setListDetailSearchTermAction, resetSearchedListContactsAction, removeSearchTermOnly } from '../../Audience/Actions/listDetailsActions';
import { resetGlobalContactsAction, resetGlobalContactsSearchedAction, setGlobalContactsSearchTermAction } from 'modules/Audience/Actions/globalContanctsActions';
import { useLocation } from 'react-router-dom';
import { globalContactsResetFiltersAction, listContactsResetFilterSearchAction } from 'modules/Audience/Actions/contactsFiltersActions';

const SearchWrapper = styled.div`
position: relative;
cursor: pointer;
  .global-search-field {
    &.ant-input-affix-wrapper-focused {
      box-shadow: none;
    }
      border-radius: 27px;
      width: 350px;
      height: 36px;
      @media all and (max-width: ${maxWidth}), ${retina} {
        height: 32px;
      }
      @media all and (max-width: ${maxWidth1300}) {
        height: 30px;
      }
      .ant-input-prefix {
        border-right: 1.5px solid #DDDDDD;
        padding-right: 6px;
        svg {
          width: 21px;
          min-width: 21px;
          height: 18px;
          @media all and (max-width: ${maxWidth}), ${retina} {
            width: 18px;
            min-width: 18px;
          }
        }
      }
      .ant-input {
        font-family: ${primaryFont};
        font-weight: ${wLight};
        font-size: 14px;
        line-height: 20px;
        padding-right: 25px;
        ::placeholder {
          font-size: 16px;
          line-height: 18px;
          font-style: italic;
          color: #C1C1C1;
          letter-spacing: -0.36px;
          @media all and (max-width: ${maxWidth}), ${retina} {
            font-size: 13px;
            line-height: 14px;
          }
        }
        @media all and (max-width: ${maxWidth1300}) {
          font-size: 12px;
          line-height: 14px;
          ::placeholder {
            font-size: 12px;
            line-height: 14px;
          }
        }
        &:focus {
          border-color: ${primaryColor};
          box-shadow: none;
          outline: none;
        }
        &:hover {
          border-color: ${primaryColor};
        }
      }
    }
  .cross-icon {
    position: absolute;
    right: 12px;
    top: 9px;
    display:block;
    cursor: pointer;
    z-index: 1;
  }
`;
type Props = {
  searchIcon?: boolean;
  page?: string;
  handleSearchProps?: (value: string) => void;
  clearSearchProps?: () => void;
  placeholderProps: string;
} & typeof defaultProps;

const defaultProps = {
  searchIcon: false,
};

const GlobalSearchBar = function GlobalSearchBar(props: Props) {
  const { searchIcon, page, handleSearchProps, clearSearchProps, placeholderProps } = props;
  const [searchText, setSearchText] = useState('');

  // reducers
  const globalContactsSearchTerm = useSelector((state: RootState) => state.globalContacts.searchTerm);
  const listSearchTerm = useSelector((state: RootState) => state.audienceListsReducerV2.searchTerm);
  const listDetailSearchTerm = useSelector((state: RootState) => state.audienceListDetailsReducerV2.searchTerm);
  const location = useLocation();
  const { pathname } = location
  const payload = { limit: 20, offset: 0, search_by_list_name: searchText.trim() };
  const inList = window.location.href.includes('list_id');
  const listsListing = window.location.pathname.includes('lists');
  const contactListing = window.location.pathname.includes('contacts');
  const previousSearchTerm = listsListing ? listSearchTerm.trim() : inList ? listDetailSearchTerm.trim() : globalContactsSearchTerm.trim();
  const onSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // console.log(e.target.value);
    var value;
    if (e.currentTarget.value.trim() !== '') {
      value = e.currentTarget.value;
    } else {
      // It won't let user enter empty space initially
      value = e.currentTarget.value.trim();
    }
    setSearchText(value);
    if (value.length === 0) {
      clearSearch();
    }
  };

  const handleSearch = () => {
    if (searchText.trim() !== '') {
      if (searchText.trim() !== previousSearchTerm) {
        setSearchText(searchText.trim());
        if (handleSearchProps) {
          handleSearchProps(searchText);
        } else {
          if (!page && listSearchTerm !== searchText) {
            // Disabling estlint for next line as promises are not required
            // eslint-disable-next-line
            getAudienceSearchedListsAction(payload);
            resetGlobalContactsSearchedAction();
            setGlobalContactsSearchTermAction(searchText.trim());
          } else if (page && page === TAB_AUDIENCE.VIEW && listDetailSearchTerm !== searchText) {
            setGlobalContactsSearchTermAction(searchText);
            setListDetailSearchTermAction(searchText);
          } else if (page && page === TAB_AUDIENCE.CONTACTS && globalContactsSearchTerm !== searchText) {
            resetGlobalContactsSearchedAction();
            setGlobalContactsSearchTermAction(searchText.trim());
          } else {
            if (listSearchTerm !== searchText) {
              // Disabling estlint for next line as promises are not required
              // eslint-disable-next-line
              getAudienceSearchedListsAction(payload);
            }
          }
        }
      }
    } else {
      setSearchText('');
    }
  };

  const clearSearch = () => {
    if (searchText !== '') setSearchText('');
    if (clearSearchProps) {
      clearSearchProps();
    } else {
      if (page && page === TAB_AUDIENCE.VIEW) {
        resetSearchedListContactsAction();
        setListDetailSearchTermAction('');
        setSearchText('');
        resetGlobalContactsSearchedAction();
        removeSearchTermOnly();
      } else if (page && page === TAB_AUDIENCE.CONTACTS) {
        resetGlobalContactsSearchedAction();
        setGlobalContactsSearchTermAction('');
        setSearchText('');
      } else if (page && page === TAB_AUDIENCE.LISTS) {
        resetAudienceSearchedList();
      } else {
        resetAudienceSearchedList();
        resetGlobalContactsSearchedAction();
        setGlobalContactsSearchTermAction('');
      }
    }
  };

  // Applicable when returning from go back button to audience page or opening contact list
  const clearSearchDefault = (page: String | undefined) => {
    // On opening the list view
    if (page && page === TAB_AUDIENCE.VIEW) {
      setSearchText('');
      resetSearchedListContactsAction();
    }
    // On opening add contacts to list
    if (page && (inList && page === TAB_AUDIENCE.CONTACTS)) {
      setSearchText('');
      resetSearchedListContactsAction();
      globalContactsResetFiltersAction();
    }
    // On returning back to audience page
    else if (!page) {
      setSearchText('');
      resetAudienceSearchedList();
      resetGlobalContactsSearchedAction();
      setGlobalContactsSearchTermAction('');
    }
  }

  useEffect(() => {
    const isClear = (pathname === '/audiences/') || page;
    if (isClear) clearSearchDefault(page);
  }, [page])

  // After adding contact to list, search bar should be cleared.
  useEffect(() => {
    if (listDetailSearchTerm === '') setSearchText('');
  }, [listDetailSearchTerm])


  return (
    <SearchWrapper className='global-search-wrapper'>
      <Input
        size='large'
        prefix={searchIcon ? <SearchIcon fillColor='#000' /> : <ResearchInvertedIcon fillColor='#000' />}
        placeholder={`Search for ${placeholderProps ? placeholderProps : listsListing ? 'lists' : 'contacts'} ${inList || listsListing || contactListing ? '' : 'or lists'}`}
        className='global-search-field'
        onChange={onSearchTextChange}
        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        value={searchText}
      />
      {searchText.length > 0 ? (<CrossIcon onClick={clearSearch} />) : null}
    </SearchWrapper>
  );
};

export default GlobalSearchBar;

GlobalSearchBar.defaultProps = defaultProps;